import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { TokenReveal } from "../../components/TokenReveal";
import { InstallScene, type InstallSceneKind } from "./InstallScene";
import styles from "./SetupWizard.module.css";

export type ConnectEdgeAgentStepProps = {
  /** The provisioned edge-agent API key, shown once on the final card; null on resume. */
  apiKey: string | null;
  /** True once an edge agent exists for the store (freshly issued, or on resume). */
  agentConnected: boolean;
  /** Custom hardware needs the manual installer step; fixed Shobdo devices ship pre-imaged. */
  isCustomHardware: boolean;
  provisioning: boolean;
  error: string | null;
  storeName: string;
  /** Whether the API key has been acknowledged (persisted, or ticked this session). */
  acknowledged: boolean;
  onAcknowledge: () => void;
  /** Whether the edge agent has reported itself online. */
  online: boolean;
  checkingOnline: boolean;
  onCheckOnline: () => void;
  onRetry: () => void;
  onContinue: () => void;
  onFinishLater: () => void;
};

const INSTALL_COMMAND = "wget -qO- https://io.shobdo.ai/install.sh | bash";
const PANEL_URL = "http://shobdo-edge.local:8088/";

type Step = { kind: InstallSceneKind; title: string; body: string };

// Each instruction is written so that nothing is left to guess: which end goes where,
// what the operator should see when the step worked, and what to do when their hardware
// differs (e.g. a box with no power button).
const buildSteps = (isCustomHardware: boolean): Step[] => [
  {
    kind: "router",
    title: "Connect the device to your router",
    body:
      "Plug one end of the ethernet cable into any free LAN port on your store router, and " +
      "the other end into the ethernet port on the edge device. The cable clicks when seated. " +
      "The port's light stays dark until the device is powered on in the next step.",
  },
  {
    kind: "power",
    title: "Power it on",
    body:
      "Plug the power adapter into a wall outlet and its connector into the device's power " +
      "port. Press the power button if your device has one — most edge boxes start as soon as " +
      "power is connected. Wait for the status light to stop blinking and settle solid; the " +
      "ethernet port's link light starts blinking too once the device is on.",
  },
  ...(isCustomHardware
    ? [
        {
          kind: "installer" as const,
          title: "Run the installer",
          body: "On the device terminal, run this one line:",
        },
      ]
    : []),
  {
    kind: "panel",
    title: "Open the setup panel",
    body: isCustomHardware
      ? "The installer prints the device's address — open it in a browser on the store " +
        "network (e.g. http://DEVICE-IP:8088) to finish on the device."
      : "On any phone or computer connected to the store's network, open the setup panel in a " +
        "browser to finish on the device.",
  },
];

/**
 * Connect Edge Agent step (wizard step 3). Walks the operator through installing the
 * device as an accordion: the current instruction fills the working area with a full
 * animated illustration; advancing collapses it to a header row, and any already-seen
 * instruction re-opens on click. The API key is revealed only on the final card and
 * gates store visibility once acknowledged; continuing to describe cameras also
 * requires the agent to report itself online.
 */
export const ConnectEdgeAgentStep = ({
  apiKey,
  agentConnected,
  isCustomHardware,
  provisioning,
  error,
  storeName,
  acknowledged,
  onAcknowledge,
  online,
  checkingOnline,
  onCheckOnline,
  onRetry,
  onContinue,
  onFinishLater,
}: ConnectEdgeAgentStepProps) => {
  const [copied, setCopied] = useState(false);
  const [active, setActive] = useState(0);
  const [maxOpened, setMaxOpened] = useState(0);
  const activeRef = useRef<HTMLLIElement | null>(null);

  const steps = buildSteps(isCustomHardware);
  const allRevealed = maxOpened >= steps.length - 1;

  // Keep the freshly-expanded instruction at the top of the viewport.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every change of `active`
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [active]);

  const openStep = (index: number): void => {
    setActive(index);
    setMaxOpened((m) => Math.max(m, index));
  };

  const copyCommand = (): void => {
    void navigator.clipboard?.writeText(INSTALL_COMMAND).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => setCopied(false),
    );
  };

  const canContinue = acknowledged && online && !provisioning;

  return (
    <div className={styles.form}>
      {error && (
        <div className={styles.banner}>
          <div>
            <div className={styles.bannerTitle}>Couldn't provision the edge agent</div>
            <div className={styles.bannerBody}>{error}</div>
          </div>
          <button
            type="button"
            className={styles.textBtn}
            onClick={onRetry}
            disabled={provisioning}
          >
            Try again
          </button>
        </div>
      )}

      <ol className={styles.inst}>
        {steps.map((s, i) => {
          const state = i === active ? "active" : i <= maxOpened ? "done" : "todo";
          return (
            <li
              key={s.kind}
              ref={i === active ? activeRef : undefined}
              className={styles.instStep}
              data-state={state}
            >
              <button
                type="button"
                className={styles.instHeader}
                onClick={() => state === "done" && openStep(i)}
                disabled={state !== "done"}
                aria-expanded={i === active}
              >
                <span aria-hidden="true" className={styles.instIndex}>
                  {state === "done" ? "✓" : i + 1}
                </span>
                <span className={styles.instTitle}>{s.title}</span>
                {state === "done" && <span className={styles.instReopen}>Review</span>}
              </button>

              {i === active && (
                <div className={styles.instBody}>
                  <div className={styles.instArt}>
                    <InstallScene kind={s.kind} />
                  </div>
                  <p className={styles.instText}>{s.body}</p>

                  {s.kind === "installer" && (
                    <>
                      <div className={styles.codeRow}>
                        <code className={styles.code}>{INSTALL_COMMAND}</code>
                        <button type="button" className={styles.textBtn} onClick={copyCommand}>
                          {copied ? "Copied ✓" : "Copy"}
                        </button>
                      </div>
                      <p className={styles.instText}>
                        Note: <code className={styles.codeInline}>-qO-</code> uses a capital letter{" "}
                        <strong>O</strong> (as in Oxygen), not a zero — easiest is to use the Copy
                        button.
                      </p>
                    </>
                  )}

                  {s.kind === "panel" && !isCustomHardware && (
                    <p className={styles.instText}>
                      Shobdo device:{" "}
                      <a className={styles.codeInline} href={PANEL_URL}>
                        {PANEL_URL}
                      </a>
                    </p>
                  )}

                  {s.kind === "panel" && apiKey && (
                    <>
                      <TokenReveal token={apiKey} />
                      <p className={styles.help}>
                        This API key for {storeName} is shown once. Save it — you'll paste it when
                        the device asks for it.
                      </p>
                      <label className={styles.checkItem}>
                        <input
                          type="checkbox"
                          checked={acknowledged}
                          disabled={acknowledged}
                          onChange={() => onAcknowledge()}
                        />
                        <span>
                          I've saved this API key somewhere safe — it won't be shown again
                        </span>
                      </label>
                    </>
                  )}

                  {s.kind === "panel" && !apiKey && agentConnected && (
                    <p className={styles.help}>
                      An edge agent is already provisioned for {storeName}. Follow the steps above
                      on the device, then come back to describe its cameras.
                    </p>
                  )}

                  {s.kind === "panel" && !apiKey && !agentConnected && provisioning && (
                    <p className={styles.help}>
                      Provisioning your edge agent and issuing its API key…
                    </p>
                  )}

                  {i < steps.length - 1 && (
                    <div className={styles.instFooter}>
                      <span className={styles.instProgress}>
                        {i + 1} of {steps.length}
                      </span>
                      <Button variant="outline" type="button" onClick={() => openStep(i + 1)}>
                        Next step →
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {allRevealed && acknowledged && (
        <div className={styles.onlineRow}>
          {online ? (
            <span className={styles.onlineOk}>✓ Device is online.</span>
          ) : (
            <>
              <span className={styles.onlineWait}>Waiting for your device to come online…</span>
              <button
                type="button"
                className={styles.textBtn}
                onClick={onCheckOnline}
                disabled={checkingOnline}
              >
                {checkingOnline ? "Checking…" : "Check now"}
              </button>
            </>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <button
            type="button"
            onClick={onFinishLater}
            disabled={provisioning}
            className={styles.textBtn}
          >
            I'll finish later
          </button>
        </div>
        <Button type="button" onClick={onContinue} disabled={!canContinue}>
          Continue → describe cameras
        </Button>
      </div>
    </div>
  );
};
